import type { StructuredDataConfig } from "@autodromo/schema-catalog";
import { getNodeDynamicData } from "../../node/getNodeDynamicData";
import type { StructuredDataEntrySource } from "../types";
import { resolveListingLinks } from "./listingLinks";

interface CollectEntrySourcesParams {
  /** Entry da página de detalhe. Ausente nas demais rotas. */
  dynamicPageContent?: Record<string, any> | null;
  /** `pageData.ROOT.dynamicContents` — a configuração de cada bloco dinâmico. */
  dynamicContents?: Record<string, unknown> | null;
  /** Resultado já filtrado, ordenado e paginado de cada bloco. */
  dynamicContentsData?: Record<string, any> | null;
  /** Árvore da página, de onde sai o link configurado em cada bloco. */
  pageData?: Record<string, any> | null;
  /**
   * Caminho do módulo da rota de item (`/seminovos`), sem o slug do registro.
   * Só essa rota conhece, e é o que dá `url` ao registro na página dele.
   */
  itemListingLink?: string | null;
}

/**
 * A configuração chega do CMS como JSON solto, então vale conferir a forma antes
 * de confiar nela. Sem entidade selecionada não há o que emitir, e o próprio
 * critério manda tratar isso como "não gera dados estruturados".
 */
function isConfigured(config: unknown): config is StructuredDataConfig {
  if (typeof config !== "object" || config === null) return false;

  const { types, mappings } = config as Partial<StructuredDataConfig>;

  return (
    Array.isArray(types) && types.length > 0 && Array.isArray(mappings)
  );
}

/**
 * Reúne os registros do CMS que a página exibe, cada um com a configuração do
 * seu tipo de conteúdo.
 *
 * A página de detalhe contribui com um item, cuja configuração viaja em
 * `_metadata.structuredData`. Cada bloco dinâmico contribui com a sua lista, e a
 * configuração vem numa chave irmã — uma cópia por bloco, não por registro. A
 * composição da chave é do `getNodeDynamicData`, o par do `setNodeDynamicData`
 * que gravou.
 *
 * Home, listagem e página estática compartilham o mesmo caminho de propósito: o
 * renderer não distingue as três, e a marcação descreve o que está na página.
 */
export function collectEntrySources({
  dynamicPageContent,
  dynamicContents,
  dynamicContentsData,
  pageData,
  itemListingLink,
}: CollectEntrySourcesParams): StructuredDataEntrySource[] {
  const sources: StructuredDataEntrySource[] = [];
  const listingLinks = resolveListingLinks(pageData);

  const itemConfig = dynamicPageContent?._metadata?.structuredData;
  if (dynamicPageContent && isConfigured(itemConfig)) {
    // Na interna o caminho vem da própria rota, não de um bloco: a página já é
    // o endereço do registro. A regra de montagem é a mesma da listagem.
    sources.push({
      config: itemConfig,
      items: [dynamicPageContent],
      ...(itemListingLink ? { listingLink: itemListingLink } : {}),
    });
  }

  Object.keys(dynamicContents ?? {})
    .filter((nodeId) => nodeId !== "ROOT")
    .forEach((nodeId) => {
      const config = getNodeDynamicData({
        nodeId,
        data: dynamicContentsData ?? undefined,
        suffix: "structuredData",
      });
      const items = getNodeDynamicData({
        nodeId,
        data: dynamicContentsData ?? undefined,
      });

      if (!isConfigured(config) || !Array.isArray(items) || items.length === 0) {
        return;
      }

      const listingLink = listingLinks[nodeId];

      sources.push({ config, items, ...(listingLink ? { listingLink } : {}) });
    });

  return sources;
}
